import React, { useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Container from '@mui/material/Container';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useWatch } from 'react-hook-form';
import { isEmpty } from 'lodash';

import * as FormInputs from 'common-components/form-inputs';
import { Lists } from 'common-components/form-inputs/list-autocomplete';

import { useSearch } from './search.ctx';
import ActivatedFilters from './activated-filters';

const SearchForm = () => {
  const {
    onSearchFormSubmit, isAdvancedSearch, isPublishSearch, clearFilters,
    isSearchLoading, selected
  } = useSearch();

  const [publishStatus] = useWatch({ name: ['publishStatus'] });

  const { t } = useTranslation();

  const [searchParams] = useSearchParams();

  if (isPublishSearch) {
    return (
      <Container maxWidth="xl">
        <ActivatedFilters hidden={!isEmpty(selected)}/>
        <Box
          style={{ color: '#04629A', cursor: 'pointer', textDecoration: "none" }}
          component={NavLink}
          to={"create"}
          className={'hidePrint'}
        >
          <AddCircleOutlineOutlinedIcon sx={{ fontSize: 14 }}/>
          {t('Create new instrument')}
        </Box>
        <Box sx={{ backgroundColor: '#e9eced' }} mt={2} p={2} className={'hidePrint'}>
          <Grid container spacing={2} component="form" onSubmit={onSearchFormSubmit}>
            <Grid item xs={5}>
              <FormInputs.FormListAutocomplete
                disableClearable
                name="publishStatus"
                listName={Lists.PublishStatus}
                label={t("Publish status")}
              />
            </Grid>
            <Grid item xs={6}>
              {publishStatus == 'published' && (
                <Stack direction={'row'} spacing={2}>
                  <FormInputs.FormDateField name="lastPublishedDateStart"/>
                  <FormInputs.FormDateField name="lastPublishedDateEnd"/>
                </Stack>
              )}
            </Grid>
            <Grid item xs={1}>
              <Button
                type="submit"
                variant="contained"
                sx={{ mr: 2 }}
                disabled={isSearchLoading}
              >
                {t('Filter')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ backgroundColor: '#e9eced' }}>
      <ActivatedFilters hidden={!isEmpty(selected)}/>
      {searchParams.get('tags') && (
        <Box className={'showInPrint'}>
          <Box style={{ fontWeight: 700 }}>
            {t('Tags')}:
          </Box>
          {searchParams.get('tags').split(';').join(', ')}
        </Box>
      )}
      <Grid
        container spacing={2} alignItems="flex-start"
        className={'hidePrint'} component="form"
        onSubmit={onSearchFormSubmit}
        sx={{ paddingBottom: '16px' }}
      >
        <Grid item xs={12} md={3}>
          <FormInputs.FormListAutocomplete
            multiple
            name="typeIds"
            listName={Lists.InstrumentTypes}
            label={t('types')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormInputs.FormListAutocomplete
            multiple
            name="committeeIds"
            listName={Lists.Committees}
            label={t('instrument.titles.committees')}
          />
        </Grid>
        {isAdvancedSearch && (
          <>
            <Grid item xs={12} md={3}>
              <FormInputs.FormTextField
                name="reference"
                label={t('reference')}
                placeholder={t('searchPlaceholder')}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormInputs.FormListAutocomplete
                name="dateType"
                listName={Lists.AdvancedSearchDateType}
                label={t("Date")}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} md={3}>
          <FormInputs.FormListAutocomplete
            multiple
            name="statusIds"
            listName={Lists.InstrumentStatusTypes}
            label={t("status")}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormInputs.FormListAutocomplete
            multiple
            name="themeIds"
            listName={Lists.Themes}
            label={t('themes')}
          />
        </Grid>
        {isAdvancedSearch && (
          <Grid item xs={12} md={3}>
            <FormInputs.FormListAutocomplete
              multiple
              name="adherentIds"
              listName={Lists.Countries}
              label={t('Adherents')}
            />
          </Grid>
        )}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            {!isAdvancedSearch && (
              <FormLabel sx={{ mb: 1 }}>
                {t('Year of adoption')}
              </FormLabel>
            )}
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <FormInputs.FormListAutocomplete
                  name="yearFrom"
                  listName={Lists.Years}
                  label={t("from")}
                />
              </Grid>
              <Grid item xs={6}>
                <FormInputs.FormListAutocomplete
                  name="yearTo"
                  listName={Lists.Years}
                  label={t("to")}
                />
              </Grid>
            </Grid>
          </FormControl>
        </Grid>
        {isAdvancedSearch && (
          <>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <FormLabel>{t('Fulltext search')}</FormLabel>
                <FormInputs.FormTextField
                  name={'term'}
                  placeholder={t('search') + "..."}
                  fullWidth
                  size="small"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormInputs.FormRadioGroup
                name="searchScope"
                label={t('Search scope')}
                options={[
                  { label: t("In all text"), value: 'fullText' },
                  { label: t('In titles'), value: 'titles' },
                ]}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} md={isAdvancedSearch ? 3 : 9} sx={{ textAlign: 'right', mt: 3 }}>
          <Button
            onClick={clearFilters}
            variant="contained"
            sx={{ mr: 2 }}
            disabled={isSearchLoading}
          >
            {t('Clear')}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSearchLoading}
          >
            {isAdvancedSearch ? t('Search') : t('Filter')}
          </LoadingButton>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SearchForm;
